/**
 * <tcr-tabs-component>
 * ---------------------------------------------------------------------------
 * Pestanas accesibles para la seccion "Soluciones recomendadas".
 *
 * Mejora progresiva estricta: Liquid renderiza TODOS los paneles en servidor,
 * cada uno con su titulo visible. Sin JavaScript la seccion se lee como una
 * lista de colecciones apiladas: nada se pierde. Este componente anade la
 * tira de pestanas, oculta los titulos duplicados y deja visible un panel.
 *
 * Custom element autonomo: no depende de ningun otro script del theme, asi que
 * no le afecta el orden de carga.
 *
 * ESCENARIO TENIDO (21/09/2026): el propio componente es el panel redondeado
 * de la seccion y toma el color de la pestana activa. `syncStage` le copia el
 * tinte y el acento del panel elegido; el CSS hace el resto (fondo con
 * transicion, tinta de la pastilla activa).
 *
 * Marcado esperado:
 *   <tcr-tabs-component data-tabs-label="Soluciones recomendadas">
 *     <div data-tabs-list></div>          <- vacio, lo rellena este script
 *     <div data-tcr-tab data-tab-label="Caida"> ... </div>
 *     <div data-tcr-tab data-tab-label="Canas"> ... </div>
 *   </tcr-tabs-component>
 *
 * EDITOR DE TEMAS
 *   - `connectedCallback` es idempotente: al mover la seccion de sitio el
 *     editor desconecta y vuelve a conectar el nodo (no lo re-renderiza), y
 *     sin vaciar la tira antes de construirla salian las pestanas por
 *     duplicado.
 *   - Escucha `shopify:block:select` (solo en `Shopify.designMode`) para
 *     activar la pestana cuyo bloque se selecciona en la barra lateral; si no,
 *     el comerciante editaba la tercera pestana viendo la primera.
 *   - Todos los listeners cuelgan de un AbortController que se aborta en
 *     `disconnectedCallback`.
 */
class TcrTabsComponent extends HTMLElement {
  /** @type {AbortController | null} */
  abortController = null;

  /** @type {HTMLButtonElement[]} */
  tabs = [];

  /** @type {HTMLElement[]} */
  panels = [];

  connectedCallback() {
    this.list = this.querySelector('[data-tabs-list]');
    this.panels = Array.from(this.querySelectorAll('[data-tcr-tab]'));

    if (!this.list || this.panels.length < 2) return;

    // Reconexion: tirar lo construido antes para no duplicar pestanas.
    this.abortController?.abort();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.list.replaceChildren();
    this.tabs = this.panels.map((panel, index) => this.createTab(panel, index));
    this.list.append(...this.tabs);
    this.list.setAttribute('role', 'tablist');

    const label = this.dataset.tabsLabel;
    if (label) this.list.setAttribute('aria-label', label);

    this.list.addEventListener('click', this.onClick, { signal });
    this.list.addEventListener('keydown', this.onKeydown, { signal });

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.onBlockSelect, { signal });
    }

    this.setAttribute('data-enhanced', '');
    this.select(0, { focus: false, instant: true });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
  }

  /**
   * @param {HTMLElement} panel
   * @param {number} index
   */
  createTab(panel, index) {
    const id = `${this.id || 'tcr-tabs'}-panel-${index}`;
    panel.id = id;
    panel.setAttribute('role', 'tabpanel');
    panel.setAttribute('aria-labelledby', `${id}-tab`);

    // Solo se anade una parada de tabulador al panel cuando no tiene nada
    // enfocable dentro (coleccion vacia): con enlaces y botones dentro, el
    // tabindex era una parada vacia de mas.
    const hasFocusable = panel.querySelector('a[href], button, input, select, textarea, [tabindex]');
    if (hasFocusable) {
      panel.removeAttribute('tabindex');
    } else {
      panel.setAttribute('tabindex', '0');
    }

    const tab = document.createElement('button');
    tab.type = 'button';
    tab.id = `${id}-tab`;
    tab.className = 'tcr-tabs__tab';
    tab.setAttribute('role', 'tab');
    tab.setAttribute('aria-controls', id);
    tab.textContent = panel.dataset.tabLabel || `${index + 1}`;

    const accent = getComputedStyle(panel).getPropertyValue('--tcr-accent');
    if (accent) tab.style.setProperty('--tcr-accent', accent.trim());

    return tab;
  }

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!target) return;

    const index = this.tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (index !== -1) this.select(index, { focus: false });
  };

  /** @param {KeyboardEvent} event */
  onKeydown = (event) => {
    const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
        next = (current - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next, { focus: true });
  };

  /**
   * Editor de temas: al seleccionar un bloque de pestana en la barra lateral,
   * se activa su panel. El evento tambien llega tras un re-render de la
   * seccion (`event.detail.load === true`), asi que cubre el caso de editar
   * un ajuste y que la seccion vuelva a la primera pestana.
   * @param {Event} event
   */
  onBlockSelect = (event) => {
    if (!this.tabs.length || !(event.target instanceof Element)) return;

    const panel = event.target.closest('[data-tcr-tab]');
    if (!panel || !this.contains(panel)) return;

    const index = this.panels.indexOf(/** @type {HTMLElement} */ (panel));
    if (index !== -1) this.select(index, { focus: false });
  };

  /**
   * @param {number} index
   * @param {{focus?: boolean, instant?: boolean}} [options]
   */
  select(index, options = {}) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      if (selected) {
        if (options.focus) tab.focus();
        this.scrollTabIntoView(tab, options.instant === true);
      }
    });

    this.panels.forEach((panel, i) => panel.toggleAttribute('hidden', i !== index));
    this.syncStage(this.panels[index]);
  }

  /**
   * El escenario toma el tinte y el acento del panel activo. Se leen del
   * propio panel (su bloque los escribe en linea o salen del CSS), asi que
   * una pestana nueva trae su color sin tocar este archivo. Un panel oculto
   * sigue teniendo sus propiedades calculadas: se puede leer aunque no se vea.
   * @param {HTMLElement | undefined} panel
   */
  syncStage(panel) {
    if (!panel) return;
    const styles = getComputedStyle(panel);
    const tint = styles.getPropertyValue('--tcr-panel-tint').trim();
    const accent = styles.getPropertyValue('--tcr-accent').trim();
    if (tint) this.style.setProperty('--tcr-stage-tint', tint);
    if (accent) this.style.setProperty('--tcr-stage-accent', accent);
  }

  /**
   * Mantiene visible la pestana activa cuando la tira desborda en movil.
   * Sin animacion al cargar y cuando el visitante pide menos movimiento.
   * @param {HTMLElement} tab
   * @param {boolean} instant
   */
  scrollTabIntoView(tab, instant) {
    if (!this.list || this.list.scrollWidth <= this.list.clientWidth) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const offset = tab.offsetLeft - (this.list.clientWidth - tab.offsetWidth) / 2;
    this.list.scrollTo({
      left: Math.max(0, offset),
      behavior: instant || reduceMotion ? 'auto' : 'smooth',
    });
  }
}

if (!customElements.get('tcr-tabs-component')) {
  customElements.define('tcr-tabs-component', TcrTabsComponent);
}
